import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Json } from '@/integrations/supabase/types';

// ============ Types ============

export type AgreementStatus = 
  | 'pending_approval'
  | 'rejected'
  | 'active'
  | 'completed'
  | 'defaulted'
  | 'superseded'
  | 'cancelled';

export type AgreementReason = 'financial_difficulty' | 'service_issue' | 'other' | null;

export type PaymentFrequency = 'weekly' | 'biweekly' | 'monthly';

export interface CustomInstallment {
  amount: number;
  due_date: string;
}

export interface PaymentAgreement {
  id: string;
  deal_id: string;
  agreement_number: number;
  original_balance: number;
  agreed_amount: number;
  num_installments: number;
  first_due_date: string | null;
  status: AgreementStatus;
  reason: AgreementReason;
  reason_notes: string | null;
  requested_by: string | null;
  approved_by: string | null;
  approved_at: string | null;
  rejection_reason: string | null;
  defaulted_at: string | null;
  completed_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  // New fields for frequency/custom
  frequency?: PaymentFrequency | null;
  custom_installments?: CustomInstallment[] | null;
  // Joined fields
  deal_name?: string;
  client_name?: string;
  contact_id?: string;
  requester_name?: string;
  approver_name?: string;
}

export interface RequestRenegotiationInput {
  deal_id: string;
  agreed_amount: number;
  num_installments: number;
  first_due_date: string;
  reason?: AgreementReason;
  reason_notes?: string;
  // New fields for frequency/custom
  frequency?: PaymentFrequency;
  custom_installments?: CustomInstallment[] | null;
}

// ============ Helpers ============

const STATUS_LABELS: Record<AgreementStatus, string> = {
  pending_approval: 'Aguardando Aprovação',
  rejected: 'Rejeitada',
  active: 'Ativo',
  completed: 'Concluído',
  defaulted: 'Inadimplente',
  superseded: 'Substituído',
  cancelled: 'Cancelado',
};

const STATUS_COLORS: Record<AgreementStatus, string> = {
  pending_approval: 'bg-amber-100 text-amber-800 border-amber-200',
  rejected: 'bg-red-100 text-red-800 border-red-200',
  active: 'bg-green-100 text-green-800 border-green-200',
  completed: 'bg-blue-100 text-blue-800 border-blue-200',
  defaulted: 'bg-red-100 text-red-800 border-red-200',
  superseded: 'bg-gray-100 text-gray-600 border-gray-200',
  cancelled: 'bg-gray-100 text-gray-500 border-gray-200',
};

const REASON_LABELS: Record<string, string> = {
  financial_difficulty: 'Dificuldade financeira',
  service_issue: 'Problema com serviço',
  other: 'Outro',
};

export const getAgreementStatusLabel = (status: AgreementStatus) => STATUS_LABELS[status] || status;
export const getAgreementStatusColor = (status: AgreementStatus) => STATUS_COLORS[status] || '';
export const getAgreementReasonLabel = (reason: AgreementReason) => 
  reason ? REASON_LABELS[reason] || reason : '';

// ============ Queries ============

/**
 * Fetch all payment agreements, optionally filtered by status
 */
export function usePaymentAgreements(status?: AgreementStatus) {
  return useQuery({
    queryKey: ['payment-agreements', status],
    queryFn: async () => {
      let query = supabase
        .from('payment_agreements')
        .select(`
          *,
          deals!inner (
            id,
            name,
            contact_id,
            contacts!fk_deals_contact (
              id,
              first_name,
              last_name
            )
          ),
          requester:app_users!payment_agreements_requested_by_fkey (
            id,
            display_name,
            first_name,
            last_name
          ),
          approver:app_users!payment_agreements_approved_by_fkey (
            id,
            display_name,
            first_name,
            last_name
          )
        `)
        .order('created_at', { ascending: false });

      if (status) {
        query = query.eq('status', status);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Map to flat structure
      return (data || []).map((row: any) => ({
        ...row,
        deal_name: row.deals?.name,
        contact_id: row.deals?.contact_id,
        client_name: row.deals?.contacts
          ? `${row.deals.contacts.first_name || ''} ${row.deals.contacts.last_name || ''}`.trim()
          : 'Cliente',
        requester_name: row.requester
          ? row.requester.display_name || `${row.requester.first_name || ''} ${row.requester.last_name || ''}`.trim()
          : null,
        approver_name: row.approver
          ? row.approver.display_name || `${row.approver.first_name || ''} ${row.approver.last_name || ''}`.trim()
          : null,
        deals: undefined,
        requester: undefined,
        approver: undefined,
      })) as PaymentAgreement[];
    },
  });
}

/**
 * Fetch all agreements for a specific deal
 */
export function usePaymentAgreementsByDeal(dealId: string) {
  return useQuery({
    queryKey: ['payment-agreements', 'deal', dealId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('payment_agreements')
        .select(`
          *,
          requester:app_users!payment_agreements_requested_by_fkey (
            id,
            display_name,
            first_name,
            last_name
          ),
          approver:app_users!payment_agreements_approved_by_fkey (
            id,
            display_name,
            first_name,
            last_name
          )
        `)
        .eq('deal_id', dealId)
        .order('agreement_number', { ascending: true });

      if (error) throw error;

      return (data || []).map((row: any) => ({
        ...row,
        requester_name: row.requester
          ? row.requester.display_name || `${row.requester.first_name || ''} ${row.requester.last_name || ''}`.trim()
          : null,
        approver_name: row.approver
          ? row.approver.display_name || `${row.approver.first_name || ''} ${row.approver.last_name || ''}`.trim()
          : null,
        requester: undefined,
        approver: undefined,
      })) as PaymentAgreement[];
    },
    enabled: !!dealId,
  });
}

/**
 * Fetch the active agreement for a deal
 */
export function useActiveAgreement(dealId: string) {
  return useQuery({
    queryKey: ['payment-agreements', 'active', dealId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('payment_agreements')
        .select('*')
        .eq('deal_id', dealId)
        .eq('status', 'active')
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;
      
      return {
        ...data,
        custom_installments: Array.isArray(data.custom_installments) 
          ? (data.custom_installments as unknown as CustomInstallment[]) 
          : null,
      } as PaymentAgreement;
    },
    enabled: !!dealId,
  });
}

// ============ Agreement Payment Summary ============

export interface AgreementPaymentSummary {
  paidAmount: number;
  paidCount: number;
  pendingAmount: number;
  pendingCount: number;
  lastPaidDate: string | null;
  maxDaysOverdue: number;
}

/**
 * Fetch payment totals for a specific agreement
 */
export function useAgreementPaymentSummary(agreementId: string | null) {
  return useQuery({
    queryKey: ['agreement-payment-summary', agreementId],
    queryFn: async (): Promise<AgreementPaymentSummary> => {
      // 1) Tentativa principal: parcelas vinculadas explicitamente ao acordo
      const { data: linked, error } = await supabase
        .from('payments')
        .select('status, amount, paid_amount, due_date, paid_at')
        .eq('agreement_id', agreementId!);

      if (error) throw error;

      let payments = linked || [];

      // 2) Fallback (parcelas legadas sem agreement_id):
      // Se não houver vínculo explícito E este acordo for o único acordo "active"
      // do deal, considere as parcelas órfãs do mesmo deal como pertencentes a ele.
      if (payments.length === 0) {
        const { data: agreement } = await supabase
          .from('payment_agreements')
          .select('id, deal_id, status')
          .eq('id', agreementId!)
          .maybeSingle();

        if (agreement?.deal_id) {
          const { data: dealAgreements } = await supabase
            .from('payment_agreements')
            .select('id, status')
            .eq('deal_id', agreement.deal_id);

          const activeAgreements = (dealAgreements || []).filter(a => a.status === 'active');
          const isOnlyActive = activeAgreements.length === 1 && activeAgreements[0].id === agreementId;

          if (isOnlyActive) {
            const { data: orphan } = await supabase
              .from('payments')
              .select('status, amount, paid_amount, due_date, paid_at')
              .eq('deal_id', agreement.deal_id)
              .is('agreement_id', null);
            payments = orphan || [];
          }
        }
      }

      const paid = payments.filter(p => p.status === 'paid');
      const pending = payments.filter(p => 
        ['pending', 'overdue', 'partially_paid'].includes(p.status || '')
      );

      // Calculate max days overdue
      const today = new Date();
      let maxDaysOverdue = 0;
      pending.forEach(p => {
        if (p.due_date) {
          const dueDate = new Date(p.due_date);
          if (dueDate < today) {
            const days = Math.floor((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));
            if (days > maxDaysOverdue) maxDaysOverdue = days;
          }
        }
      });

      // Find last paid date
      const paidWithDates = paid.filter(p => p.paid_at);
      let lastPaidDate: string | null = null;
      if (paidWithDates.length > 0) {
        paidWithDates.sort((a, b) => 
          new Date(b.paid_at!).getTime() - new Date(a.paid_at!).getTime()
        );
        lastPaidDate = paidWithDates[0].paid_at!;
      }

      return {
        paidAmount: paid.reduce((sum, p) => sum + (p.paid_amount || 0), 0),
        paidCount: paid.length,
        pendingAmount: pending.reduce((sum, p) => sum + ((p.amount || 0) - (p.paid_amount || 0)), 0),
        pendingCount: pending.length,
        lastPaidDate,
        maxDaysOverdue,
      };
    },
    enabled: !!agreementId,
  });
}

/**
 * Rede de segurança: saldo em aberto do DEAL inteiro (todas as parcelas
 * pending/overdue/partially_paid), independente de existir acordo vinculado.
 * Usado na renegociação quando o acordo não traz saldo (ex.: parcelas criadas
 * sem acordo original), evitando cair no valor de uma única parcela.
 */
export function useDealOpenBalance(dealId: string | null) {
  return useQuery({
    queryKey: ['deal-open-balance', dealId],
    queryFn: async (): Promise<{ openAmount: number; openCount: number }> => {
      const { data, error } = await supabase
        .from('payments')
        .select('amount, paid_amount, status')
        .eq('deal_id', dealId!)
        .in('status', ['pending', 'overdue', 'partially_paid']);

      if (error) throw error;

      const rows = data || [];
      return {
        openAmount: rows.reduce((sum, p) => sum + ((p.amount || 0) - (p.paid_amount || 0)), 0),
        openCount: rows.length,
      };
    },
    enabled: !!dealId,
  });
}



// ============ Mutations ============

/**
 * Request a renegotiation (creates a new agreement with pending_approval status)
 */
export function useRequestRenegotiation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: RequestRenegotiationInput) => {
      const { data, error } = await (supabase as any).rpc('request_renegotiation_secured', {
        p_deal_id: input.deal_id,
        p_agreed_amount: input.agreed_amount,
        p_num_installments: input.num_installments,
        p_first_due_date: input.first_due_date,
        p_reason: input.reason || null,
        p_reason_notes: input.reason_notes || null,
        p_frequency: input.frequency || 'monthly',
        p_custom_installments: (input.custom_installments as unknown as Json) || null,
      });

      if (error) throw error;
      return data as string; // UUID of new agreement
    },
    onSuccess: () => {
      toast.success('Solicitação de renegociação enviada');
      queryClient.invalidateQueries({ queryKey: ['payment-agreements'] });
    },
    onError: (error: any) => {
      console.error('Error requesting renegotiation:', error);
      toast.error(error.message || 'Erro ao solicitar renegociação');
    },
  });
}

/**
 * Approve a renegotiation (marks old payments as renegotiated, creates new payments)
 */
export function useApproveRenegotiation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (agreementId: string) => {
      const { data, error } = await (supabase as any).rpc('approve_renegotiation_secured', {
        p_agreement_id: agreementId,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Renegociação aprovada! Novas parcelas criadas automaticamente.');
      queryClient.invalidateQueries({ queryKey: ['payment-agreements'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['collection-dashboard'] });
    },
    onError: (error: any) => {
      console.error('Error approving renegotiation:', error);
      toast.error(error.message || 'Erro ao aprovar renegociação');
    },
  });
}

/**
 * Reject a renegotiation
 */
export function useRejectRenegotiation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      agreementId, 
      rejectionReason 
    }: { 
      agreementId: string; 
      rejectionReason: string;
    }) => {
      const { data, error } = await (supabase as any).rpc('reject_renegotiation_secured', {
        p_agreement_id: agreementId,
        p_rejection_reason: rejectionReason,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Renegociação rejeitada');
      queryClient.invalidateQueries({ queryKey: ['payment-agreements'] });
    },
    onError: (error: any) => {
      console.error('Error rejecting renegotiation:', error);
      toast.error(error.message || 'Erro ao rejeitar renegociação');
    },
  });
}
